import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Microscope, Loader2, Sparkles } from "lucide-react";
import { api } from "../../lib/api";
import { apiErrorMessage } from "../../lib/errors";
import ResearchResultCard from "./ResearchResultCard";

const SPORTS = [
  { key: "mlb", label: "MLB" },
  { key: "nba", label: "NBA" },
  { key: "nhl", label: "NHL" },
  { key: "nfl", label: "NFL" },
  { key: "mma", label: "UFC/MMA" },
];

function todayYYYYMMDD() {
  const d = new Date();
  return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}${String(d.getDate()).padStart(2, "0")}`;
}

function compactToDateInput(compact) {
  if (!compact || compact.length !== 8) return "";
  return `${compact.slice(0, 4)}-${compact.slice(4, 6)}-${compact.slice(6, 8)}`;
}

function ControlsRow({ sport, setSport, date, setDate, gameId, setGameId, games, loadingGames, onRun, running }) {
  return (
    <div className="grid md:grid-cols-12 gap-3">
      <div className="md:col-span-3">
        <div className="overline mb-2">SPORT</div>
        <select
          value={sport}
          onChange={(e) => setSport(e.target.value)}
          data-testid="research-sport-select"
          className="w-full bg-zinc-950 border border-zinc-800 px-3 py-2.5 mono text-sm focus:border-[#00D4FF] focus:outline-none"
        >
          {SPORTS.map((s) => (
            <option key={s.key} value={s.key}>
              {s.label}
            </option>
          ))}
        </select>
      </div>
      <div className="md:col-span-3">
        <div className="overline mb-2">DATE</div>
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          data-testid="research-date-input"
          className="w-full bg-zinc-950 border border-zinc-800 px-3 py-2.5 mono text-sm focus:border-[#00D4FF] focus:outline-none"
        />
      </div>
      <div className="md:col-span-4">
        <div className="overline mb-2">
          GAME {loadingGames && <Loader2 size={10} className="animate-spin inline ml-1" />}
        </div>
        <select
          value={gameId}
          onChange={(e) => setGameId(e.target.value)}
          disabled={loadingGames || games.length === 0}
          data-testid="research-game-select"
          className="w-full bg-zinc-950 border border-zinc-800 px-3 py-2.5 mono text-sm focus:border-[#00D4FF] focus:outline-none disabled:opacity-50"
        >
          {loadingGames && (
            <option value="">Loading games…</option>
          )}
          {!loadingGames && games.length === 0 && (
            <option value="">No games on this date</option>
          )}
          {!loadingGames && games.map((g) => (
            <option key={g.id} value={g.id}>
              {g.short_name || g.matchup}
            </option>
          ))}
        </select>
      </div>
      <div className="md:col-span-2 flex items-end">
        <button
          onClick={onRun}
          disabled={running || !gameId}
          data-testid="research-run-button"
          className="w-full px-4 py-2.5 bg-[#00D4FF] text-black mono text-xs font-bold tracking-wider hover:bg-[#00D4FF]/90 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {running ? (
            <>
              <Loader2 size={14} className="animate-spin" />
              ANALYZING…
            </>
          ) : (
            <>
              <Sparkles size={14} strokeWidth={2} />
              RUN ANALYSIS
            </>
          )}
        </button>
      </div>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="mt-6 border border-zinc-800 p-8 text-center" data-testid="research-loading">
      <Loader2 size={24} className="animate-spin mx-auto text-[#00D4FF] mb-3" />
      <div className="overline">SIMULATING 1,000 GAMES</div>
      <p className="mono text-xs text-zinc-500 mt-2">
        Pulling DraftKings prices, season-series form, and venue context…
      </p>
    </div>
  );
}

function PanelHeader() {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
      <div>
        <div className="overline neon-text mb-2 flex items-center gap-2">
          <Microscope size={12} strokeWidth={1.75} />
          DEEP RESEARCH · CLAUDE 4.5
        </div>
        <h2 className="font-display text-3xl lg:text-4xl leading-tight">
          Pick a game. Get a <span className="text-[#00D4FF]">1,000-game</span> read.
        </h2>
        <p className="text-sm text-zinc-400 mt-2 max-w-2xl">
          Every evaluation is grounded in live ESPN data, DraftKings prices, and the
          2025/2026 season series. The model returns a fair line, edge vs market, and
          a strict Martingale-viability call.
        </p>
      </div>
    </div>
  );
}

export default function ResearchAnalyst() {
  const [sport, setSport] = useState("mlb");
  const [date, setDate] = useState(compactToDateInput(todayYYYYMMDD()));
  const [games, setGames] = useState([]);
  const [gameId, setGameId] = useState("");
  const [loadingGames, setLoadingGames] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const compactDate = useMemo(() => date.replaceAll("-", ""), [date]);

  const loadGames = useCallback(async (sportKey, compact, signal) => {
    setLoadingGames(true);
    setGames([]);
    setGameId("");
    try {
      const { data } = await api.get("/research/games", {
        params: { sport: sportKey, date: compact || undefined },
        signal,
      });
      setGames(data.games || []);
      if ((data.games || []).length > 0) {
        setGameId(data.games[0].id);
      }
    } catch (err) {
      if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") return;
      console.warn("research games load failed", err);
      toast.error(apiErrorMessage(err, "Could not load games"));
      setGames([]);
    } finally {
      setLoadingGames(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    loadGames(sport, compactDate, controller.signal);
    return () => controller.abort();
  }, [sport, compactDate, loadGames]);

  const onRun = useCallback(async () => {
    if (!gameId) {
      toast.error("Pick a game first");
      return;
    }
    setRunning(true);
    setResult(null);
    try {
      const { data } = await api.post("/research/evaluate", {
        sport,
        game_id: gameId,
        date: compactDate || undefined,
      });
      setResult(data);
      toast.success("Research card ready");
    } catch (err) {
      const status = err?.response?.status;
      if (status === 402) {
        toast.error("Start your 3-day free trial to unlock the Research Analyst");
      } else {
        toast.error(apiErrorMessage(err, "Could not generate research"));
      }
    } finally {
      setRunning(false);
    }
  }, [sport, gameId, compactDate]);

  const selectedGame = games.find((g) => g.id === gameId);

  return (
    <section
      className="border border-zinc-800 bg-zinc-950/40 p-6 lg:p-8 mb-10"
      data-testid="research-analyst"
    >
      <PanelHeader />
      <ControlsRow
        sport={sport}
        setSport={setSport}
        date={date}
        setDate={setDate}
        gameId={gameId}
        setGameId={setGameId}
        games={games}
        loadingGames={loadingGames}
        onRun={onRun}
        running={running}
      />

      {selectedGame && (
        <div className="mono text-[11px] text-zinc-500 mt-3">
          {selectedGame.venue} · {new Date(selectedGame.start_time).toLocaleString()}
        </div>
      )}

      {running && <LoadingState />}

      {result && !running && (
        <div className="mt-6">
          <ResearchResultCard result={result} />
        </div>
      )}
    </section>
  );
}
